import { FirebaseService, ToastService } from "@shared/lib/services";
import { Agency } from "@shared/lib/types/agency";
import {
  Button,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Upload,
} from "antd";
import { useState } from "react";

import { ACCOUNT_STATUS } from "@shared/lib/types";
import styles from "./styles.module.scss";

interface AgencyFormProps {
  initialValues?: Agency;
  onSubmit: (values: Partial<Agency>) => void;
  isSubmitting: boolean;
}

export function AgencyForm({
  initialValues,
  onSubmit,
  isSubmitting,
}: AgencyFormProps) {
  const [form] = Form.useForm();
  const [profilePicUrl, setProfilePicUrl] = useState(
    initialValues?.profilePicUrl || ""
  );

  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS>(
    initialValues?.status || ACCOUNT_STATUS.ACTIVE
  );

  const [type, setType] = useState(initialValues?.type || "Company");

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validImageTypes.includes(file.type)) {
      ToastService.error("Only image files are allowed.");
      return;
    }

    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "agency-logos",
        file
      );
      setProfilePicUrl(uploadedUrl);
      form.setFieldValue("profilePicUrl", uploadedUrl);
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    }
  };

  const handleDeleteImage = () => {
    setProfilePicUrl("");
    form.setFieldValue("profilePicUrl", "");
    ToastService.success("Logo removed successfully.");
  };

  const handleSubmit = (values: any) => {
    onSubmit({
      ...values,
      profilePicUrl,
      ...(initialValues?.status ? { status: accountStatus } : {}),
      type: type,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        name: initialValues?.name || "",
        companyName: initialValues?.companyName || "",
        profilePicUrl: initialValues?.profilePicUrl || "",
        type: type,
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <Form.Item
        label="Agency Logo"
        name="profilePicUrl"
        rules={[{ required: true, message: "Agency logo is required" }]}
      >
        <Space direction="vertical">
          <Upload
            onChange={(info) =>
              info.file && handleImageUpload(info.file.originFileObj!)
            }
            showUploadList={false}
            accept="image/jpeg,image/jpg,image/png"
            beforeUpload={(file) => {
              const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(
                file.type
              );
              if (!isImage) {
                ToastService.error(
                  "You can only upload JPG, JPEG, or PNG files!"
                );
              }
              return isImage; // Prevents upload if not a valid image
            }}
          >
            <Button>Upload Logo</Button>
          </Upload>
          {profilePicUrl && (
            <div>
              <Image src={profilePicUrl} alt="Agency Logo" width={100} />
              <Popconfirm
                title="Are you sure to delete this logo?"
                onConfirm={handleDeleteImage}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger>
                  Delete Logo
                </Button>
              </Popconfirm>
            </div>
          )}
        </Space>
      </Form.Item>

      <Form.Item
        name="name"
        label="Agency Name"
        rules={[{ required: true, message: "Agency name is required" }]}
      >
        <Input placeholder="Agency Name" />
      </Form.Item>

      <Form.Item
        name="companyName"
        label="Company Name"
        rules={[{ required: true, message: "Company name is required" }]}
      >
        <Input placeholder="Company Name" />
      </Form.Item>

      <Form.Item
        name="type"
        label="Type"
        rules={[{ required: true, message: "Type is required" }]}
      >
        <Select
          defaultValue={type}
          placeholder="Type"
          onChange={(value) => {
            setType(value);
          }}
          options={[
            {
              label: "Individual",
              value: "Individual",
            },
            {
              label: "Company",
              value: "Company",
            },
          ]}
        />{" "}
      </Form.Item>

      {initialValues?.status && (
        <Form.Item label="Disable">
          <Row justify="space-between">
            <div className={styles.diasbledText}>
              Disable this agency contact.
            </div>
            <Switch
              onChange={(checked) => {
                setAccountStatus(
                  checked ? ACCOUNT_STATUS.INACTIVE : ACCOUNT_STATUS.ACTIVE
                );
              }}
              checked={accountStatus === ACCOUNT_STATUS.INACTIVE}
            />
          </Row>
        </Form.Item>
      )}

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Form.Item>
    </Form>
  );
}
